import { useEffect } from 'react';
import { useWebSocket } from '../context/WebSocketContextUtil';
import { SubscriptionScope } from '../types/websocket';

/**
 * Hook to subscribe to WebSocket events for a specific scope
 * Automatically subscribes on mount and unsubscribes on unmount
 *
 * @param scope - The subscription scope (region, division, etc.)
 * @param enabled - Whether the subscription is active (default: true)
 *
 * @example
 * ```tsx
 * // Subscribe to all events in a region
 * useWebSocketSubscription({
 *   type: 'region',
 *   regionId: selectedRegion.id
 * });
 * ```
 */
export function useWebSocketSubscription(scope: SubscriptionScope | null, enabled = true) {
  const { subscribe, unsubscribe, isConnected } = useWebSocket();

  // Depend on the scope's *content*, not its object identity. Callers pass a
  // fresh object literal every render (e.g. `{ type: 'realization_session', … }`),
  // so keying the effect on the object reference re-runs it on every re-render —
  // tearing down and re-creating the backend subscription. Any message that lands
  // during that unsubscribed gap is dropped (this stranded the activation modal:
  // the final `provisioning_result` arrived mid-churn and never reached the UI).
  const scopeKey = scope ? JSON.stringify(scope) : null;

  useEffect(() => {
    if (!enabled || scopeKey === null || !isConnected) {
      return;
    }

    const activeScope = JSON.parse(scopeKey) as SubscriptionScope;
    subscribe(activeScope);

    // Unsubscribe on unmount or when the scope's content actually changes.
    return () => {
      unsubscribe(activeScope);
    };
  }, [scopeKey, enabled, isConnected, subscribe, unsubscribe]);
}

/**
 * Hook to subscribe to a region's events
 * Convenience wrapper around useWebSocketSubscription
 *
 * @param regionId - The region ID to subscribe to
 * @param enabled - Whether the subscription is active (default: true)
 */
export function useRegionSubscription(regionId: string | null | undefined, enabled = true) {
  useWebSocketSubscription(
    regionId ? { type: 'region', regionId } : null,
    enabled && !!regionId
  );
}

/**
 * Hook to subscribe to a division's events
 * Convenience wrapper around useWebSocketSubscription
 *
 * @param divisionId - The division ID to subscribe to
 * @param enabled - Whether the subscription is active (default: true)
 */
export function useDivisionSubscription(divisionId: string | null | undefined, enabled = true) {
  useWebSocketSubscription(
    divisionId ? { type: 'division', divisionId } : null,
    enabled && !!divisionId
  );
}

/**
 * Hook to subscribe to organization-wide events
 * Convenience wrapper around useWebSocketSubscription
 *
 * @param enabled - Whether the subscription is active (default: true)
 */
export function useOrganizationSubscription(enabled = true) {
  useWebSocketSubscription({ type: 'organization' }, enabled);
}
